import {
  Box,
  Flex,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { char2entity } from 'src/utils/common';
import SmartTooltip from '../../Report/SmartTooltip';
import { LogTypeEnum } from '@Types/log';
import { useVirusLinks } from '@Log/context/virusLink';
import { useLogReducerContext } from '../context';
import { LogTimeUnit } from '../shared';
import { riskAiLogLink } from '../../Quarantine/RiskAI/utils';
import { MISDIRECTED_SECURITY_FILTER_NAME } from '../constants';
import { useMisdirectedDetailStore } from '../MisdirectedDetailDrawer/store';

export const MergedSecurityRiskName = ({ logItem }: { logItem: any }) => {
  const { t } = useTranslation();
  const {
    state: { timeRange, type },
  } = useLogReducerContext();
  const openMisdirectedDrawer = useMisdirectedDetailStore((s) => s.openDrawer);
  const {
    mergedSecurityRiskName: content,
    securityFilterName,
    securityFilterNameKey,
    mdSessionId,
    spamSnapReport,
    threatTypeKey,
    blockReason,
  } = logItem;

  if (type !== LogTypeEnum.DETECTION || !content) {
    return <SmartTooltip>{content}</SmartTooltip>;
  }

  // CAS-4153: Misdirected detection rows open the detail drawer.
  if (
    securityFilterNameKey === MISDIRECTED_SECURITY_FILTER_NAME &&
    mdSessionId
  ) {
    return (
      <Link
        onClick={(e) => {
          e.preventDefault();
          openMisdirectedDrawer(mdSessionId);
        }}
      >
        {content}
      </Link>
    );
  }

  if (threatTypeKey === 'Suspicious Detect') {
    const needTooltipUnusualSignalArray = [
      'Account-Takeover',
      'Unusual-URL',
      'Payment-PDF-Free-Email',
      'Payment-HTML-Free-Email',
      'Payment-HTML-NB-Account',
      'Forged-Brand',
      'Suspicious-Notify',
    ];
    if (needTooltipUnusualSignalArray.includes(content)) {
      return (
        <Popover placement="left" trigger="hover">
          <PopoverTrigger>
            <Link>{content}</Link>
          </PopoverTrigger>
          <PopoverContent
            PopperProps={{
              usePortal: true,
            }}
            flexWrap="wrap"
            wordBreak="break-all"
            maxWidth="480px"
          >
            <Text>{t(`quarantine.unusualSignal.tooltip.${content}`)}</Text>
          </PopoverContent>
        </Popover>
      );
    }
  }

  if (spamSnapReport) {
    return <BECReport stringifiedReports={spamSnapReport} content={content} />;
  }

  if (securityFilterName === t('riskAi.title')) {
    const isCustomTimeRange = timeRange.predefined === LogTimeUnit.Custom;
    const start = isCustomTimeRange ? timeRange.start : undefined;
    const end = isCustomTimeRange ? timeRange.end : undefined;
    const mergedSecurityRiskNameLink = riskAiLogLink({
      securityRiskName: logItem['mergedSecurityRiskName'],
      riskType: logItem['raiRiskType'],
      orgId: logItem['companyGuid'] ?? 'all',
      timeRange: timeRange.predefined,
      timeRangeStart: start,
      timeRangeEnd: end,
    });
    return <InnerLink link={mergedSecurityRiskNameLink} content={content} />;
  }

  if (blockReason) {
    const blockReasonStr = String(blockReason ?? '');
    if (
      logItem.scanSourceKey === 'googledrive' &&
      blockReasonStr.includes('Over restriction')
    ) {
      return <SmartTooltip>{content}</SmartTooltip>;
    }
    return <VirusLink blockReason={blockReason} content={content} />;
  }

  return <SmartTooltip>{content}</SmartTooltip>;
};

const BECReport = ({ stringifiedReports, content }: any) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const { t } = useTranslation();
  if (!stringifiedReports) {
    return <></>;
  }
  let reports: {
    feature_id: number;
    keywords: string[] | string;
  }[];
  if (!Array.isArray(stringifiedReports)) {
    try {
      reports = JSON.parse(stringifiedReports);
    } catch (error) {
      console.error('parse BEC report string error!');
      return <></>;
    }
  } else {
    reports = stringifiedReports;
  }

  const getKeywords = (keywords: string[] | string) => {
    const ret: { [key: string]: string } = {};
    let i = 1;
    if (Array.isArray(keywords)) {
      keywords.forEach((item) => (ret[`param${i++}`] = char2entity(item)));
    } else {
      ret[`param1`] = char2entity(keywords);
    }
    return ret;
  };

  return (
    <Popover placement="left" trigger="hover">
      <PopoverTrigger>
        <Link>{content}</Link>
      </PopoverTrigger>
      <PopoverContent
        PopperProps={{
          usePortal: true,
        }}
        flexWrap="wrap"
        wordBreak="break-all"
      >
        <Flex direction="column" color={colorStyle.color.primary}>
          <Text fontWeight="bold">{t('log.becReport.header')}</Text>
          {reports.map((report) => {
            const keywords = getKeywords(report.keywords);
            return (
              <Box key={report.feature_id} pb="12px">
                <Text>{t(`log.becReport.${report.feature_id}.title`)}:</Text>
                <Text>
                  {t(`log.becReport.${report.feature_id}.body`, keywords)}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </PopoverContent>
    </Popover>
  );
};

const VirusLink = ({
  blockReason,
  content,
}: {
  blockReason: any;
  content: string;
}) => {
  const virusLinks = useVirusLinks();

  if (virusLinks[blockReason]) {
    return <InnerLink link={virusLinks[blockReason]} content={content} />;
  }
  return <SmartTooltip>{content}</SmartTooltip>;
};

const InnerLink = ({ link, content }: { link: string; content: string }) => {
  const { t } = useTranslation();
  return (
    <Link
      width="100%"
      href={link}
      target="_blank"
      title={t('log.mergedSecurityRiskName.malwareScanningLink')}
      rel="noopener noreferrer"
    >
      <SmartTooltip>{content}</SmartTooltip>
    </Link>
  );
};
